import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { Box, IconButton } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import PreviewIcon from "@mui/icons-material/Preview";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import PostAddIcon from "@mui/icons-material/PostAdd";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { ChartIcon } from "Components/Common/Styled/ChartIcon";
import ArticleIcon from "@mui/icons-material/Article";
import { uniqueId } from "lodash";
import PropTypes from "prop-types";

import {
  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
  FIRE_EMERGENCY_SR_MANAGER,
  FIRE_SAFETY_SUPER_ADMIN,
  fireSafetyApplicationStatusTypes,
  fireSafetyScheduleStatusTypes,
} from "common/constant";
import Tooltip from "Components/Common/Tooltip";
import Modal from "Components/Common/Modal";
import ApplicationStatusHistoryChart from "Components/Charts/ApplicationStatusHistoryChart";
import DeveloperBuilding from "Components/FireSafetyBuildingViewModal/DeveloperBuilding";
import AuditSchedularApproval from "./AuditSchedularApproval";
import SchedularApprovalModal from "./SchedularApprovalModal";
import FireSafetyBuildingObservationDetails from "./FireSafetyBuildingObservationDetails";
import FireSafetyBuildingObservation from "./FireSafetyBuildingObservation";
import FireSafetyViewObservationNotice from "./FireSafetyViewObservationNotice";
import FireSafetyGenerateObservationImages from "./FireSafetyGenerateObservationImages";
import FireSafetyBuildingInformationTracking from "./FireSafetyBuildingInformationTracking";

import { getImageUrl, isAuthorizedUser } from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import { ApplicationIcon } from "./style";
import GenarteNoticeModal from "./GenarteNoticeModal";
import {
  useLazyBuildingScheduleQuery,
  useLazyGetFireSafetyObservationNotice,
} from "apis/FireSafety/FireSafetyObservation";
import NoticeActionButtons from "./NoticeActionButtons";
import NoticePreviewModal from "Components/Common/ViewApplicationDetailComponents/NoticePreviewModal";
import Backdrop from "Components/Common/Backdrop";

const convertDate = (date) =>
  new Date(convertUTCDateToLocalDate(date).format("YYYY-MM-DD"));

const FireSafetyApplicationAdminActions = ({
  t,
  item,
  isBuildingObservationSideBarSelected,
  viewApplicationBtnHandler,
}) => {
  const {
    id,
    isChangedByUser,
    developerBuilding,
    reScheduleEndDate,
    isScheduleAdded,
    isNoticeCanGenerate,
    reScheduleStartDate,
    scheduleStatus,
    isAcknowledged,
    isObservationNoticeSend,
    isAllObservationApproved,
    buildingInformationStatus: { id: applicationStatusId },
    scheduleId,
  } = item;

  const { roles } = useSelector((state) => state.Login);
  const [searchParams, setSearchParams] = useSearchParams();
  const applicationIdForAuditSchedular = +searchParams.get("auditScheduleId");
  const [applicationIdForAuditSchedule, setApplicationIdForAuditSchedule] =
    useState("");
  const [
    applicationIdForDefaultSchedular,
    setApplicationIdForDefaultSchedular,
  ] = useState("");
  const [isBuildingObservationModalOpen, setIsBuildingObservationModalOpen] =
    useState(false);
  const [applicationIdForChartModal, setApplicationIdForChartModal] =
    useState("");
  const [isGenrateNoticeModal, setIsGenrateNoticeModal] = useState(null);
  const [noticePreview, setNoticePreview] = useState(null);

  const [
    isBuildingObservationTabModalOpen,
    setIsBuildingObservationTabModalOpen,
  ] = useState(false);
  const [isViewNoticeModalOpen, setIsViewNoticeModalOpen] = useState(false);
  const [isGenerateModalVisible, setIsGenerateModalVisible] = useState(false);
  const [
    buildingInformationTrackingModalOpen,
    setBuildingInformationTrackingModalOpen,
  ] = useState(false);
  const [isBuildingAssignModalOpen, setIsBuildingAssignModalOpen] =
    useState(false);

  const [
    getBuildingScheduleById,
    {
      isLoading: isScheduleDataLoading,
      isFetching: isScheduleDataFetching,
      data: { data: buildingSchedulesData } = [],
    },
  ] = useLazyBuildingScheduleQuery();

  const [
    getNotice,
    { data: { data: noticeData } = {}, isLoading: isNoticeLoading },
  ] = useLazyGetFireSafetyObservationNotice();

  const isApprovedApplicationStatus = useMemo(
    () => applicationStatusId === fireSafetyApplicationStatusTypes["Approved"],
    [applicationStatusId]
  );

  const isAuditSchedularIconVisible = useMemo(
    () =>
      !isBuildingObservationSideBarSelected &&
      isApprovedApplicationStatus &&
      !!isScheduleAdded &&
      isChangedByUser &&
      !isAcknowledged &&
      isAuthorizedUser(roles, [FIRE_EMERGENCY_CHIEF_FIRE_OFFICER]),
    [
      isBuildingObservationSideBarSelected,
      isApprovedApplicationStatus,
      isScheduleAdded,
      isChangedByUser,
      isAcknowledged,
    ]
  );

  const handleCloseForApprove = () => {
    setIsBuildingAssignModalOpen(false);
  };

  useEffect(() => {
    if (
      applicationIdForAuditSchedular === developerBuilding?.id &&
      !applicationIdForAuditSchedule &&
      isAuditSchedularIconVisible
    ) {
      setApplicationIdForAuditSchedule(id);
    }
    if (
      !isAuditSchedularIconVisible &&
      developerBuilding?.id === applicationIdForAuditSchedular
    ) {
      searchParams.delete("auditScheduleId");
      setSearchParams(searchParams);
    }
  }, [
    applicationIdForAuditSchedular,
    developerBuilding?.id,
    applicationIdForAuditSchedule,
    isAuditSchedularIconVisible,
  ]);

  const handleViewNotice = async () => {
    let pdfUrl = "";
    try {
      const scheduleData = await getBuildingScheduleById(id);
      const scheduleID = scheduleData?.data?.[0]?.scheduleId;

      if (scheduleID) {
        const res = await getNotice(scheduleID);
        pdfUrl = res?.data?.url;
      }
      setNoticePreview({
        url: pdfUrl ? getImageUrl(pdfUrl) : null,
        documentTypeName: "Generated Notice",
        documentType: "pdf",
        documentName: "GeneratedNotice",
      });
    } catch (error) {}
  };
  if (isScheduleDataLoading || isScheduleDataFetching || isNoticeLoading) {
    return <Backdrop open />;
  }

  return (
    <Box width="150px">
      {/* Audit Schedule Approval */}
      {!!applicationIdForAuditSchedule &&
        !!applicationIdForAuditSchedular &&
        developerBuilding?.id === applicationIdForAuditSchedular && (
          <AuditSchedularApproval
            applicationId={applicationIdForAuditSchedule}
            developerBuilding={developerBuilding}
            handleClose={() => {
              setApplicationIdForAuditSchedule("");
              searchParams.delete("auditScheduleId");
              setSearchParams(searchParams);
            }}
            events={
              isApprovedApplicationStatus && !isAcknowledged
                ? [
                    {
                      id: uniqueId(),
                      start: convertDate(reScheduleStartDate),
                      end: convertDate(reScheduleEndDate),
                      type: "consumer",
                      status: scheduleStatus,
                      title: developerBuilding?.name ?? "",
                    },
                  ]
                : []
            }
          />
        )}

      {isBuildingAssignModalOpen && (
        <DeveloperBuilding
          applicationId={id}
          isBuildingNotAssignedBySrManager
          handleClose={handleCloseForApprove}
          buildingName={developerBuilding?.name ?? ""}
        />
      )}

      {!!applicationIdForDefaultSchedular && (
        <SchedularApprovalModal
          applicationId={applicationIdForDefaultSchedular}
          buildingName={developerBuilding?.name ?? ""}
          handleClose={() => setApplicationIdForDefaultSchedular("")}
        />
      )}

      {isBuildingObservationModalOpen && (
        <Modal
          isOpen
          handleClose={() => setIsBuildingObservationModalOpen(false)}
          maxWidth="lg"
          title={`Building Observations - ${developerBuilding?.name ?? ""}`}
        >
          <FireSafetyBuildingObservation
            applicationId={id}
            handleClose={() => setIsBuildingObservationModalOpen(false)}
            scheduleId={scheduleId}
            isObservationNoticeSend={isObservationNoticeSend}
          />
        </Modal>
      )}

      {isBuildingObservationTabModalOpen && (
        <FireSafetyBuildingObservationDetails
          applicationId={id}
          handleClose={() => setIsBuildingObservationTabModalOpen(false)}
          buildingName={developerBuilding?.name ?? ""}
          isObservationNoticeSend={isObservationNoticeSend}
        />
      )}

      {isViewNoticeModalOpen && (
        <FireSafetyViewObservationNotice
          applicationId={id}
          handleClose={() => setIsViewNoticeModalOpen(false)}
          buildingName={developerBuilding?.name ?? ""}
        />
      )}
      {buildingInformationTrackingModalOpen && (
        <FireSafetyBuildingInformationTracking
          applicationId={id}
          handleClose={() => setBuildingInformationTrackingModalOpen(false)}
        />
      )}

      {isGenerateModalVisible && (
        <FireSafetyGenerateObservationImages
          applicationId={id}
          handleClose={() => setIsGenerateModalVisible(false)}
          buildingName={developerBuilding?.name ?? ""}
        />
      )}

      {!!applicationIdForChartModal && (
        <Modal
          isOpen
          handleClose={() => setApplicationIdForChartModal("")}
          title="Application Status History Chart"
          maxWidth="md"
        >
          <ApplicationStatusHistoryChart
            applicationId={applicationIdForChartModal}
            isFireSafety
          />
        </Modal>
      )}

      {isGenrateNoticeModal && (
        <GenarteNoticeModal
          setIsGenrateNoticeModal={setIsGenrateNoticeModal}
          scheduleId={scheduleId}
        />
      )}

      {!!noticePreview && (
        <NoticePreviewModal
          isOpen
          handleClose={() => {
            setNoticePreview(null);
          }}
          actionButton={
            <NoticeActionButtons
              noticeData={noticeData}
              setNoticePreview={setNoticePreview}
            />
          }
          {...noticePreview}
        />
      )}

      {/* View icon for application */}
      <IconButton onClick={() => viewApplicationBtnHandler(id)}>
        <Tooltip title={t("View Application Details")}>
          <RemoveRedEyeIcon
            fontSize="small"
            color="primary"
            className="cursor-pointer"
          />
        </Tooltip>
      </IconButton>

      {/* if building name not selected, Status is approved and role is CFO */}
      {!developerBuilding?.name &&
        isAuthorizedUser(roles, [FIRE_EMERGENCY_CHIEF_FIRE_OFFICER]) &&
        isApprovedApplicationStatus && (
          <IconButton onClick={() => setIsBuildingAssignModalOpen(true)}>
            <Tooltip title={t("Assign Building")}>
              <AddCircleOutlineIcon
                color="primary"
                fontSize="small"
                className="cursor-pointer"
              />
            </Tooltip>
          </IconButton>
        )}

      {/* Audit Schedule */}
      {!isBuildingObservationSideBarSelected &&
        !!developerBuilding?.name &&
        isApprovedApplicationStatus &&
        !isScheduleAdded &&
        isAuthorizedUser(roles, [FIRE_EMERGENCY_CHIEF_FIRE_OFFICER]) &&
        (!scheduleStatus ||
          [
            fireSafetyScheduleStatusTypes["In Progress"],
            fireSafetyScheduleStatusTypes["Pending"],
          ].includes(scheduleStatus)) && (
          <IconButton onClick={() => setApplicationIdForDefaultSchedular(id)}>
            <Tooltip title={t("Audit Schedule")}>
              <CalendarTodayIcon
                color="primary"
                fontSize="small"
                className="cursor-pointer"
              />
            </Tooltip>
          </IconButton>
        )}

      {/* Audit Schedule */}
      {isAuditSchedularIconVisible && (
        <IconButton
          onClick={() => {
            setApplicationIdForAuditSchedule(id);
            searchParams.set("auditScheduleId", developerBuilding.id);
            setSearchParams(searchParams);
          }}
        >
          <Tooltip title={t("Audit Schedular Approval")}>
            <CalendarMonthIcon
              color="primary"
              fontSize="small"
              className="cursor-pointer"
            />
          </Tooltip>
        </IconButton>
      )}

      {/* Building Observation */}
      {((isBuildingObservationSideBarSelected &&
        isAuthorizedUser(roles, [FIRE_EMERGENCY_CHIEF_FIRE_OFFICER])) ||
        !!isAcknowledged) &&
        (!isObservationNoticeSend ||
          isAuthorizedUser(roles, [FIRE_EMERGENCY_CHIEF_FIRE_OFFICER])) &&
        !!developerBuilding?.name &&
        isApprovedApplicationStatus && (
          <IconButton
            onClick={() =>
              isBuildingObservationSideBarSelected
                ? setIsBuildingObservationTabModalOpen(true)
                : setIsBuildingObservationModalOpen(true)
            }
          >
            <Tooltip title={t("Building Observation")}>
              <ContentPasteSearchIcon
                color="primary"
                fontSize="small"
                className="cursor-pointer"
              />
            </Tooltip>
          </IconButton>
        )}

      {!!isNoticeCanGenerate &&
        isAuthorizedUser(roles, [FIRE_EMERGENCY_SR_MANAGER]) &&
        isApprovedApplicationStatus && (
          <IconButton onClick={() => setIsGenrateNoticeModal(true)}>
            <Tooltip title={t("Genrate Notice")}>
              <PostAddIcon
                color="primary"
                fontSize="small"
                className="cursor-pointer"
              />
            </Tooltip>
          </IconButton>
        )}

      <IconButton
        onClick={() => {
          searchParams.set("buildingObservationId", id);
          setSearchParams(searchParams);
        }}
      >
        <Tooltip title={t("View Building Observation")}>
          <ApplicationIcon
            color="primary"
            fontSize="small"
            className="cursor-pointer"
          />
        </Tooltip>
      </IconButton>

      {isBuildingObservationSideBarSelected && (
        <IconButton
          onClick={() => {
            // setIsViewNoticeModalOpen(true);
            handleViewNotice();
          }}
        >
          <Tooltip title={t("View Notice")}>
            <PreviewIcon
              fontSize="small"
              color="primary"
              className="cursor-pointer"
            />
          </Tooltip>
        </IconButton>
      )}

      {isBuildingObservationSideBarSelected && isAllObservationApproved && (
        <IconButton
          onClick={() => {
            setIsGenerateModalVisible(true);
          }}
        >
          <Tooltip
            title={t(
              `${
                isAuthorizedUser(roles, [
                  FIRE_SAFETY_SUPER_ADMIN,
                  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
                  FIRE_EMERGENCY_SR_MANAGER,
                ])
                  ? "Generate & "
                  : ""
              }View Observation Comparison Photographs`
            )}
          >
            <AddPhotoAlternateIcon
              fontSize="small"
              color="primary"
              className="cursor-pointer"
              sx={{ fontSize: 22 }}
            />
          </Tooltip>
        </IconButton>
      )}

      {!isBuildingObservationSideBarSelected && (
        <IconButton
          onClick={() => {
            setBuildingInformationTrackingModalOpen(true);
          }}
        >
          <Tooltip title={t("Building Information Tracking")}>
            <ArticleIcon
              color="primary"
              fontSize="medium"
              className="cursor-pointer"
            />
          </Tooltip>
        </IconButton>
      )}

      {/* View Application History Chart */}
      <IconButton onClick={() => setApplicationIdForChartModal(id)}>
        <Tooltip title={t("Application Status History Chart")}>
          <ChartIcon />
        </Tooltip>
      </IconButton>
    </Box>
  );
};

FireSafetyApplicationAdminActions.propTypes = {
  t: PropTypes.func,
  item: PropTypes.object,
  isBuildingObservationSideBarSelected: PropTypes.bool,
  viewApplicationBtnHandler: PropTypes.func,
};

export default withTranslation()(FireSafetyApplicationAdminActions);
